/**
 * Bruksfrekvens per kart-celle («hot cells»).
 *
 * Sporer hvor ofte brukeren faktisk ser på et område (0,05°-celler, se utils/grid).
 * Signalet brukes to steder for smartere buffring:
 *   - Spot-cache: hot celler serveres lenger fra lokal cache før vi refresher mot nett
 *     (mindre «masse lasting» i områder du bruker mye).
 *   - Tile-eviction: fliser i hot celler beskyttes mot LRU-eviction (områder du
 *     besøker periodisk aldres ikke ut mellom besøkene).
 *
 * Persisteres i IndexedDB, men speiles i minnet så oppslag kan være SYNKRONE
 * (Leaflet createTile er synkron). Minnet hydreres fra IDB ved oppstart.
 */
import { gridKeyForBoundsCenter, gridKeyForTile } from '../utils/grid';
import type { MapBounds } from '../types';

export interface RegionUsageEntry {
  count: number;
  lastSeen: number;
}

export interface RegionUsageStore {
  getAll(): Promise<Array<{ key: string } & RegionUsageEntry>>;
  set(key: string, entry: RegionUsageEntry): Promise<void>;
}

/** En celle regnes «hot» fra dette antallet registrerte besøk. */
export const HOT_THRESHOLD = 3;
/** Minste tid mellom to tellinger for SAMME celle (unngår at panorering blåser opp tallet). */
const RECORD_THROTTLE_MS = 20_000;
/** Tak på vekten en celle kan bidra med (så et superhot område ikke dominerer alt). */
export const MAX_WEIGHT = 10;

const DB_NAME = 'fricamp_region_usage';
const STORE_NAME = 'cells';

// --- In-memory speil (kilde for SYNKRONE oppslag) ---
const _counts = new Map<string, RegionUsageEntry>();
const _lastRecorded = new Map<string, number>();
let _hydrated = false;
let _hydrating: Promise<void> | null = null;

// --- Injiserbar store (IDB i prod, in-memory i test) ---
let _store: RegionUsageStore | null = null;

function idbStore(): RegionUsageStore {
  let dbPromise: Promise<IDBDatabase> | null = null;
  const openDB = (): Promise<IDBDatabase> => {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return dbPromise;
  };
  return {
    async getAll() {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const out: Array<{ key: string } & RegionUsageEntry> = [];
        const req = store.openCursor();
        req.onsuccess = () => {
          const cur = req.result;
          if (cur) {
            const v = cur.value as RegionUsageEntry;
            out.push({ key: String(cur.key), count: v.count, lastSeen: v.lastSeen });
            cur.continue();
          } else resolve(out);
        };
        req.onerror = () => reject(req.error);
      });
    },
    async set(key, entry) {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put(entry, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    },
  };
}

function store(): RegionUsageStore | null {
  if (_store) return _store;
  if (typeof indexedDB === 'undefined') return null; // ingen persistens (SSR/test uten IDB) — kun minne
  try {
    _store = idbStore();
    return _store;
  } catch {
    return null;
  }
}

/** Last frekvens-tellingene fra IDB inn i minnet én gang. Idempotent. */
export async function ensureHydrated(): Promise<void> {
  if (_hydrated) return;
  if (_hydrating) return _hydrating;
  _hydrating = (async () => {
    const s = store();
    if (s) {
      try {
        const all = await s.getAll();
        for (const e of all) {
          if (!_counts.has(e.key)) _counts.set(e.key, { count: e.count, lastSeen: e.lastSeen });
        }
      } catch {
        // best-effort
      }
    }
    _hydrated = true;
  })();
  return _hydrating;
}

/**
 * Registrer at brukeren ser på et område. Teller opp SENTER-cellen (representerer
 * fokuset), strupet per celle så kontinuerlig panorering i ett område ikke blåser
 * opp tallet. Fire-and-forget persistens.
 */
export function recordView(bounds: MapBounds): void {
  if (!bounds) return;
  const key = gridKeyForBoundsCenter(bounds);
  const now = Date.now();
  const last = _lastRecorded.get(key) || 0;
  if (now - last < RECORD_THROTTLE_MS) return;
  _lastRecorded.set(key, now);

  const prev = _counts.get(key) || { count: 0, lastSeen: 0 };
  const next: RegionUsageEntry = { count: prev.count + 1, lastSeen: now };
  _counts.set(key, next);

  const s = store();
  if (s) void s.set(key, next).catch(() => {});
}

/** Synkron vekt for en cellenøkkel: antall besøk, tak MAX_WEIGHT. 0 hvis ukjent. */
export function getCellWeightSync(cellKey: string): number {
  const e = _counts.get(cellKey);
  if (!e) return 0;
  return Math.min(MAX_WEIGHT, e.count);
}

/** Synkron vekt for en XYZ-flis (mapper flis → celle). */
export function getTileWeightSync(x: number, y: number, z: number): number {
  return getCellWeightSync(gridKeyForTile(x, y, z));
}

/** Er senter-cellen i dette utsnittet «hot»? Synkron. */
export function isBoundsHotSync(bounds: MapBounds): boolean {
  const e = _counts.get(gridKeyForBoundsCenter(bounds));
  return !!e && e.count >= HOT_THRESHOLD;
}

/**
 * Fra hvor mange besøk en celle er verdt å foreslå offline-nedlasting for. Høyere
 * enn HOT_THRESHOLD — dette er «du er her ofte nok til at det er verdt lagringen».
 */
export const OFFLINE_SUGGEST_THRESHOLD = 8;

/**
 * Ren beslutning: hvilken celle (om noen) bør vi foreslå offline-nedlasting for?
 * Den mest brukte cellen over terskelen som IKKE alt er foreslått. `isSuggested`
 * injiseres (localStorage i prod) så funksjonen er testbar.
 */
export function pickOfflineSuggestion(
  isSuggested: (key: string) => boolean,
  threshold: number = OFFLINE_SUGGEST_THRESHOLD,
): { key: string; count: number; lastSeen: number } | null {
  for (const cell of getTopCells(5)) {
    if (cell.count < threshold) break; // sortert synkende → resten er også under
    if (!isSuggested(cell.key)) return cell;
  }
  return null;
}

/** De `n` mest brukte cellene (høyest count først). Synkron, fra minnet. */
export function getTopCells(n: number = 1): Array<{ key: string; count: number; lastSeen: number }> {
  return [...
    [..._counts.entries()].map(([key, v]) => ({ key, count: v.count, lastSeen: v.lastSeen }))
  ]
    .sort((a, b) => b.count - a.count || b.lastSeen - a.lastSeen)
    .slice(0, Math.max(0, n));
}

// --- Test-hooks ---
export function __setStoreForTest(s: RegionUsageStore | null): void {
  _store = s;
}
export function __resetForTest(): void {
  _counts.clear();
  _lastRecorded.clear();
  _hydrated = false;
  _hydrating = null;
  _store = null;
}

// Hydrer i bakgrunnen ved import (best-effort) så synkrone oppslag har data
// fra og med andre økt. Ingen effekt i test (IDB mangler / overstyres).
if (typeof indexedDB !== 'undefined') {
  void ensureHydrated();
}
